import React, { useEffect, useRef, useCallback, useMemo } from "react";
import { sessionService } from "../../services/sessionService";
import { useWebSocketContext } from "../../contexts/WebSocketContext";
import { WebSocketMessage, MessageType } from "../../types/websocket";
import "./ChatInterface.css";

interface SessionLogPanelProps {
  sessionId?: string;
  className?: string;
  maxHeight?: string;
}

// Type guard helper for message data
const hasProperty = (obj: unknown, prop: string): obj is Record<string, unknown> => {
  return typeof obj === "object" && obj !== null && prop in obj;
};

const getMessageDataProperty = (data: unknown, prop: string): string => {
  if (hasProperty(data, prop) && typeof data[prop] === "string") {
    return data[prop] as string;
  }
  return "";
};

export const SessionLogPanel: React.FC<SessionLogPanelProps> = ({
  sessionId,
  className = "",
  maxHeight = "100%",
}) => {
  const { isConnected, getLogMessages, prependMessages, getUserUsername, refreshUserCache } =
    useWebSocketContext();

  const logEndRef = useRef<HTMLDivElement>(null);

  // Get log-relevant messages from the unified WebSocket store
  const messages = getLogMessages(sessionId);

  // Filter and sort for the narrative log view
  const sessionLog = useMemo(() => {
    return [...messages].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  }, [messages]);

  const senderIds = useMemo(() => {
    return Array.from(
      new Set(
        sessionLog
          .map((msg) => msg.senderId)
          .filter((senderId): senderId is string => Boolean(senderId))
      )
    );
  }, [sessionLog]);

  const loadSessionLog = useCallback(async () => {
    if (!sessionId) return;
    try {
      // Fetch newest-first log from API
      const resp = await sessionService.getSessionLog(sessionId, 100);
      if (resp.log && resp.log.length > 0) {
        // Map persisted messages to WebSocketMessage shape
        const historical: WebSocketMessage[] = resp.log.map((m) => {
          let type: MessageType = "chat_ic";
          if (m.message_type === "dice_roll") type = "dice_roll";
          if (m.message_type === "system") type = "chat_system";

          return {
            id: `history-${m.id}`,
            type,
            sessionId: String(m.session_id),
            senderId: String(m.user_id),
            timestamp: m.sent_at,
            data:
              m.message_type === "dice_roll"
                ? {
                    formula: m.content,
                    characterName: m.character_name,
                    senderUsername: m.sender_username,
                    isHistorical: true,
                  }
                : {
                    message: m.content,
                    characterName: m.character_name,
                    senderUsername: m.sender_username,
                    messageType: m.message_type,
                    isHistorical: true,
                  },
          };
        });

        // Prepend to context (it will deduplicate by ID)
        prependMessages(historical);
      }
    } catch (err) {
      console.error("Failed to load session log:", err);
    }
  }, [sessionId, prependMessages]);

  // Load history on mount or when sessionId changes
  useEffect(() => {
    if (isConnected && sessionId) {
      loadSessionLog();
    }
  }, [isConnected, sessionId, loadSessionLog]);

  // Extract unique user IDs and refresh cache
  useEffect(() => {
    if (senderIds.length > 0) refreshUserCache(senderIds);
  }, [senderIds, refreshUserCache]);

  // Auto-scroll to bottom when new entries arrive
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [sessionLog.length]);

  const renderLogEntry = (msg: WebSocketMessage, index: number) => {
    const time = new Date(msg.timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    const characterName = getMessageDataProperty(msg.data, "characterName");
    const senderUsername =
      getMessageDataProperty(msg.data, "senderUsername") || getUserUsername(msg.senderId);
    const sender = characterName || senderUsername || "Unknown";

    const content =
      getMessageDataProperty(msg.data, "message") ||
      getMessageDataProperty(msg.data, "formula") ||
      "";

    if (msg.type === "chat_system" || msg.type === "system_alert") {
      const sysContent = getMessageDataProperty(msg.data, "message") || "System notification";
      return (
        <div key={`log-${msg.id}-${index}`} className="chat-message system-message">
          <div className="flex items-start space-x-2">
            <span className="text-gray-400 text-xs min-w-[40px]">{time}</span>
            <span className="text-yellow-400 text-sm italic">📢 {sysContent}</span>
          </div>
        </div>
      );
    }

    if (msg.type === "dice_roll") {
      const totalVal =
        hasProperty(msg.data, "total") && typeof msg.data.total === "number" ? msg.data.total : 0;
      const rollName = getMessageDataProperty(msg.data, "rollName");

      return (
        <div
          key={`log-${msg.id}-${index}`}
          className="chat-message dice-roll-message border-l-4 border-indigo-500 pl-2 py-1 my-0.5 bg-indigo-950/30 rounded-r"
        >
          <div className="flex items-start space-x-2">
            <span className="text-gray-400 text-xs min-w-[40px]">{time}</span>
            <span className="text-sm">
              <span className="text-indigo-300 font-semibold">🎲 {sender}</span>
              {rollName && <span className="text-yellow-300 ml-1">({rollName})</span>}
              <span className="text-gray-400 mx-1">rolled</span>
              <span className="font-mono text-gray-200">{content}</span>
              {totalVal > 0 && (
                <>
                  <span className="text-gray-500 mx-1">→</span>
                  <span className="text-blue-300 font-bold">{totalVal}</span>
                </>
              )}
            </span>
          </div>
        </div>
      );
    }

    // Default: IC message
    return (
      <div key={`log-${msg.id}-${index}`} className="chat-message ic-message">
        <div className="flex items-start space-x-2">
          <span className="text-gray-400 text-xs min-w-[40px]">{time}</span>
          <div className="flex-1">
            <span className="font-semibold text-blue-300">{sender}:</span>
            <span className="ml-2 text-blue-200">{content}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`chat-interface flex flex-col bg-zinc-900 ${className}`}
      data-testid="session-log-panel"
    >
      <div className="flex-1 p-3 overflow-y-auto chat-messages codex-scroll" style={{ maxHeight }}>
        {sessionLog.length === 0 ? (
          <div className="text-zinc-500 text-sm italic text-center py-4">
            No log entries yet. IC messages, public dice rolls, and system updates appear here.
          </div>
        ) : (
          sessionLog.map((entry, index) => renderLogEntry(entry, index))
        )}
        <div ref={logEndRef} />
      </div>
    </div>
  );
};
